import React, { useEffect, useState, useRef } from 'react';
import { FaPhone } from 'react-icons/fa';

function Reviews() {
  // State for tracking which reviews are visible for animation
  const [visibleReviews, setVisibleReviews] = useState([]);
  const reviewsRef = useRef(null);
  const gridRef = useRef(null);

  // Sample reviews data
  const reviews = [
    {
      id: 1,
      name: "בעלי מסעדת פסטה בסטה",
      photo: "/photos/defaultUser.jpg",
      rating: 5,
      feedback: "בינה לבנייה הקימו לנו את המסעדה מאפס - מעבודות השלד ועד הגמרים. הצוות מקצועי, עמד בלוחות הזמנים והתוצאה מדהימה. ממליצים בחום!",
      project: "הקמת מסעדה - קרית מוצקין"
    },
    {
      id: 2,
      name: "משפחת גורדון",
      photo: "/photos/defaultUser.jpg",
      rating: 5,
      feedback: "שיפוץ מקיף של הפנטהאוס שלנו ברחוב גורדון. העבודה בוצעה ברמה גבוהה מאוד, עם תשומת לב לכל פרט. התוצאה עלתה על הציפיות!",
      project: "שיפוץ פנטהאוס - נתניה"
    },
    {
      id: 3,
      name: "ועד בית הכנסת מקדונלד",
      photo: "/photos/defaultUser.jpg",
      rating: 5,
      feedback: "שיפוץ והקמת חדר הרב בבית הכנסת בוצעו במקצועיות רבה. הצוות היה רגיש לצרכים המיוחדים של המקום והתוצאה מכובדת ויפה.",
      project: "שיפוץ חדר רב - נתניה"
    },
    {
      id: 4,
      name: "משפחת תבורי",
      photo: "/photos/defaultUser.jpg",
      rating: 5,
      feedback: "מהפך מלא בבית הפרטי שלנו בכפר תבור. מהתכנון ועד הביצוע - הכל היה מושלם. הבית נראה חדש לגמרי. תודה רבה!",
      project: "שיפוץ בית פרטי - כפר תבור"
    },
    {
      id: 5,
      name: "משפחת שבא",
      photo: "/photos/defaultUser.jpg",
      rating: 5,
      feedback: "קיבלנו דירת קבלן חדשה והצוות של בינה לבנייה הפך אותה לבית חלומות. עיצוב פנים מרהיב ועבודות גמר ברמה הגבוהה ביותר.",
      project: "עיצוב דירת קבלן - נתניה"
    }
  ];

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add visible class with delay for each review
            const reviewElements = entry.target.querySelectorAll('.review-card');
            reviewElements.forEach((review, index) => {
              setTimeout(() => {
                setVisibleReviews(prev => [...prev, index]);
              }, index * 200); // Stagger animation by 200ms
            });
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of section is visible
    );

    if (reviewsRef.current) {
      observer.observe(reviewsRef.current);
    }

    return () => {
      if (reviewsRef.current) {
        observer.unobserve(reviewsRef.current);
      }
    };
  }, []);

  // Function to render star rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        className={`star ${index < rating ? 'filled' : ''}`}
      >
        ★
      </span>
    ));
  };

  // Scroll functions for horizontal scrolling
  const scrollLeft = () => {
    if (gridRef.current) {
      gridRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (gridRef.current) {
      gridRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  return (
    <section className="reviews-section" ref={reviewsRef}>
      {/* Section Header */}
      <div className="reviews-header">
        <img src="/logo/logo.png" alt="לוגו בינה" className="reviews-logo" />
        <h2>מה הלקוחות שלנו אומרים</h2>
        <p>המלצות מלקוחות מרוצים שהאמינו בנו ובחרו בעבודה מקצועית ואיכותית</p>
      </div>

      {/* Reviews Grid */}
      <div className="reviews-container">
        <button
          className="reviews-scroll-indicator left"
          onClick={scrollLeft}
          aria-label="גלול שמאלה"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="reviews-grid" ref={gridRef}>
          {reviews.map((review, index) => (
            <div
              key={review.id}
              className={`review-card ${visibleReviews.includes(index) ? 'visible' : ''}`}
            >
              {/* Client Photo */}
              <div className="client-photo">
                <img src={review.photo} alt={review.name} />
              </div>

              {/* Review Content */}
              <div className="review-content">
                {/* Star Rating */}
                <div className="rating">
                  {renderStars(review.rating)}
                </div>

                {/* Client Feedback */}
                <blockquote className="feedback">
                  "{review.feedback}"
                </blockquote>

                {/* Client Info */}
                <div className="client-info">
                  <h4 className="client-name">{review.name}</h4>
                  <p className="project-type">{review.project}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          className="reviews-scroll-indicator right"
          onClick={scrollRight}
          aria-label="גלול ימינה"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Call to Action */}
      <div className="reviews-cta">
        <p>מוכנים להתחיל את הפרויקט שלכם?</p>
        <button
          className="cta-button"
          onClick={() => {
            const element = document.getElementById('contact');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          צור קשר עכשיו <FaPhone style={{ marginLeft: '8px' }} />
        </button>
      </div>
    </section>
  );
}

export default Reviews;